import { createFileRoute, Link, Outlet } from '@tanstack/react-router'
import { Checkbox, Tooltip } from '@mantine/core'
import { AdminScopeProvider, useAdminScope } from './-admin/adminScope'
import '@/styles/pages.css'
import './-admin/admin.css'

export const Route = createFileRoute('/_app/organization/admin')({
  head: () => ({ meta: [{ title: 'Administration · AERRIS' }] }),
  component: () => (
    <AdminScopeProvider>
      <AdministrationShell />
    </AdminScopeProvider>
  ),
})

interface TabDef {
  id: string
  label: string
  to: string
  /** Tabs with no Client-independent form are disabled in global-default mode. */
  clientOnly?: boolean
}

const TABS: TabDef[] = [
  { id: 'taxonomy', label: 'Taxonomy', to: '/organization/admin/taxonomy' },
  { id: 'prompts', label: 'Prompts', to: '/organization/admin/prompts' },
  { id: 'risk', label: 'Risk', to: '/organization/admin/risk' },
  { id: 'access', label: 'Access', to: '/organization/admin/access' },
  { id: 'runs', label: 'Runs', to: '/organization/admin/runs', clientOnly: true },
  { id: 'versions', label: 'Versions', to: '/organization/admin/versions' },
]

function AdministrationShell() {
  const { isGlobalAdmin, globalMode, setGlobalMode, selectedClient, scopeName } = useAdminScope()

  return (
    <div className="admin-shell">
      <aside className="admin-rail" aria-label="Administration sections">
        <div className="admin-rail__header">
          <h1 className="admin-rail__title">Administration</h1>
          <p className="admin-scope">
            {globalMode ? (
              <>Editing <strong className="admin-scope--global">the global default template</strong></>
            ) : selectedClient ? (
              <>Editing <strong>{selectedClient.name}</strong></>
            ) : (
              <>No client selected</>
            )}
          </p>
        </div>

        <nav className="admin-rail__nav">
          {TABS.map((tab) => {
            const disabled = tab.clientOnly && globalMode
            if (disabled) {
              return (
                <Tooltip key={tab.id} label="Select a client (not available for the global default)" withArrow>
                  <span className="admin-rail__item admin-rail__item--disabled" aria-disabled="true">{tab.label}</span>
                </Tooltip>
              )
            }
            return (
              <Link
                key={tab.id}
                to={tab.to}
                search={(prev) => prev}
                className="admin-rail__item"
                activeProps={{ className: 'admin-rail__item admin-rail__item--active' }}
                activeOptions={{ exact: false }}
              >
                {tab.label}
              </Link>
            )
          })}
        </nav>

        {isGlobalAdmin && (
          <div className="admin-rail__footer">
            <Checkbox
              className="admin-global-toggle"
              color="red"
              label={<span className="admin-global-toggle__label">Global default</span>}
              checked={globalMode}
              onChange={(e) => setGlobalMode(e.currentTarget.checked)}
            />
          </div>
        )}
      </aside>

      <div className="admin-outlet" key={globalMode ? 'global' : scopeName}>
        <Outlet />
      </div>
    </div>
  )
}
